import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ShieldAlert, Receipt, RefreshCw, Loader2, AlertTriangle, Link2, Crosshair, Camera } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { ApprovazioniService } from '../services/apiService';
import { PendingApprovalsView } from './PendingApprovalsView';
import { ApprovazioneSpeseView } from './ApprovazioneSpeseView';
import { IncarichiDaAbbinareView } from './IncarichiDaAbbinareView';
import { AllertOrariDifformiModal } from '../components/AllertOrariDifformiModal';
import { AllertGeofenceModal } from '../components/AllertGeofenceModal';
import { AllertMediaModal } from '../components/AllertMediaModal';
import { subscribeApprovazioniChanged, emitApprovazioniRefreshRequested } from '../utils/approvazioniEvents';

// Hub Approvazioni: tab per Media e per "Spese & Lavori" (i report giornalieri
// includono già le spese dei collaboratori). Le pagine /pending-approvals e
// /amministrazione/approvazione-spese restano raggiungibili come deep-link;
// qui vengono montate in un container che mostra anche i conteggi.

type TabId = 'media' | 'lavori-spese' | 'da-abbinare';

const TABS: { id: TabId; label: string; icon: React.ReactElement; }[] = [
    { id: 'media',         label: 'Media',          icon: <ShieldAlert size={16}/> },
    { id: 'lavori-spese',  label: 'Lavori & Spese', icon: <Receipt    size={16}/> },
    // Incarichi urgenti aperti dal collaboratore senza mandato: dopo il report
    // il titolare li abbina al fascicolo corretto.
    { id: 'da-abbinare',   label: 'Da Abbinare',    icon: <Link2      size={16}/> },
];

export const ApprovazioniView: React.FC = () => {
    const { currentSedeId } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const initialTab = (searchParams.get('tab') as TabId) || 'media';
    const [activeTab, setActiveTab] = useState<TabId>(
        TABS.some(t => t.id === initialTab) ? initialTab : 'media'
    );

    const [summary, setSummary] = useState({ media: 0, lavori: 0, spese: 0, daAbbinare: 0, totale: 0 });
    const [loadingSummary, setLoadingSummary] = useState(true);
    // Storico difformità orarie incarichi (default ultimi 7 giorni)
    const [showAllertModal, setShowAllertModal] = useState(false);
    // Storico alert geofence (start/stop turno fuori dal raggio)
    const [showGeofenceModal, setShowGeofenceModal] = useState(false);
    // Foto arrivate dalla coda offline dell'app con EXIF assente o fuori tolleranza (km comprese)
    const [showMediaExifModal, setShowMediaExifModal] = useState(false);

    const loadSummary = useCallback(async () => {
        setLoadingSummary(true);
        try {
            const s = await ApprovazioniService.getSummary(currentSedeId || 'ALL');
            setSummary(s);
        } catch {
            // Il riepilogo è solo informativo: le tab caricano i propri dati da sole
        } finally {
            setLoadingSummary(false);
        }
    }, [currentSedeId]);

    useEffect(() => { loadSummary(); }, [loadSummary]);

    // Ricarica i conteggi quando un sotto-componente approva/rifiuta/abbina,
    // così i badge delle tab restano allineati.
    useEffect(() => subscribeApprovazioniChanged(() => { loadSummary(); }), [loadSummary]);

    const handleTabChange = (id: TabId) => {
        setActiveTab(id);
        // Tab nella query string, così la pagina è linkabile
        const next = new URLSearchParams(searchParams);
        next.set('tab', id);
        setSearchParams(next, { replace: true });
    };

    const tabCount = (id: TabId): number => {
        if (id === 'media') return summary.media;
        if (id === 'da-abbinare') return summary.daAbbinare;
        return summary.lavori + summary.spese;
    };

    return (
        <div className="min-h-screen bg-slate-50 pb-12">
            {/* Header */}
            <div className="max-w-6xl mx-auto px-4 lg:px-6 pt-4 lg:pt-6 pb-3">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <h1 className="text-2xl font-bold text-slate-900">Approvazioni</h1>
                        <p className="text-sm text-slate-500 mt-0.5">
                            {loadingSummary ? (
                                <span className="inline-flex items-center gap-1 text-slate-400">
                                    <Loader2 size={12} className="animate-spin"/> Aggiornamento...
                                </span>
                            ) : (
                                <>
                                    <span className="font-bold text-slate-700">{summary.totale}</span>
                                    {' elementi in attesa · '}
                                    {summary.media} media · {summary.lavori} lavori · {summary.spese} spese · {summary.daAbbinare} da abbinare
                                </>
                            )}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setShowAllertModal(true)}
                            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-bold text-rose-700 bg-rose-50 hover:bg-rose-100 border border-rose-200 rounded-xl shadow-sm transition-colors"
                            title="Apri storico orari difformi (incarichi)"
                        >
                            <AlertTriangle size={15} />
                            <span className="hidden sm:inline">Allert orari difformi</span>
                        </button>
                        <button
                            onClick={() => setShowGeofenceModal(true)}
                            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-bold text-rose-700 bg-rose-50 hover:bg-rose-100 border border-rose-200 rounded-xl shadow-sm transition-colors"
                            title="Apri storico allert geofence (start/stop fuori raggio)"
                        >
                            <Crosshair size={15} />
                            <span className="hidden sm:inline">Allert geofence</span>
                        </button>
                        <button
                            onClick={() => setShowMediaExifModal(true)}
                            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-bold text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-xl shadow-sm transition-colors"
                            title="Foto caricate offline con data di scatto assente o incoerente (solo informativo)"
                        >
                            <Camera size={15} />
                            <span className="hidden sm:inline">Avvisi foto offline</span>
                        </button>
                        <button
                            onClick={() => { loadSummary(); emitApprovazioniRefreshRequested(); }}
                            className="p-2.5 text-slate-500 hover:text-slate-800 bg-white rounded-xl shadow-sm border border-slate-200 transition-colors"
                            title="Aggiorna conteggi e liste"
                        >
                            <RefreshCw size={16} className={loadingSummary ? 'animate-spin' : ''}/>
                        </button>
                    </div>
                </div>
            </div>
            {showAllertModal && <AllertOrariDifformiModal onClose={() => setShowAllertModal(false)} />}
            {showGeofenceModal && <AllertGeofenceModal onClose={() => setShowGeofenceModal(false)} />}
            {showMediaExifModal && <AllertMediaModal onClose={() => setShowMediaExifModal(false)} />}

            {/* Tabs */}
            <div className="max-w-6xl mx-auto px-4 lg:px-6 overflow-x-auto">
                <div className="flex gap-1 bg-white p-1 rounded-xl border border-slate-200 shadow-sm w-fit">
                    {TABS.map(t => {
                        const count = tabCount(t.id);
                        const active = activeTab === t.id;
                        return (
                            <button
                                key={t.id}
                                onClick={() => handleTabChange(t.id)}
                                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-colors ${
                                    active
                                        ? 'bg-blue-600 text-white shadow-sm'
                                        : 'text-slate-500 hover:bg-slate-100'
                                }`}
                            >
                                {t.icon}
                                {t.label}
                                {count > 0 && (
                                    <span className={`text-[10px] font-black rounded-full px-1.5 py-0.5 ${
                                        active ? 'bg-white text-blue-700' : 'bg-rose-100 text-rose-700'
                                    }`}>
                                        {count > 99 ? '99+' : count}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Le pagine esistenti fanno già le proprie fetch e seguono `currentSedeId`. */}
            <div className="mt-4">
                {activeTab === 'media'         && <PendingApprovalsView />}
                {activeTab === 'lavori-spese'  && <ApprovazioneSpeseView />}
                {activeTab === 'da-abbinare'   && <IncarichiDaAbbinareView />}
            </div>
        </div>
    );
};

export default ApprovazioniView;
